import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Properties } from 'csstype'
import { Loader } from './components'
import { Box } from './core-components'

type RegisterResult = { accepted: boolean; unregister: () => void }
type Ctx = {
  registerOwnerLoader: (el: Element) => RegisterResult
  setHexColor: (color: string | undefined) => void
  size: { width: number; height: number }
}

const PersistentLoaderContext = createContext<Ctx | null>(null)

const idleMs = 200

type ProviderProps = {
  children: React.ReactNode
}

const PersistentLoaderProviderInner = ({ children }: ProviderProps) => {
  const [mounted, setMounted] = useState<boolean>(true)
  const ownerLoaderRef = useRef<boolean>(false)
  const idleTimerRef = useRef<number | null>(null)
  const [hexColor, setHexColorState] = useState<string>()
  const [size, setSize] = useState<{ width: number; height: number } | null>(null)
  const [persistentLoaderWrapperStyle, setPersistentLoaderWrapperStyle] = useState(
    () =>
      ({
        position: 'fixed',
        top: '0px',
        left: '0px',
        width: '0px',
        height: '0px',
        visibility: 'hidden',
        opacity: '1',
        pointerEvents: 'none',
        zIndex: '2147483647',
      }) as Properties<string | number, string & {}>
  )

  const persistentLoaderCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return
      if (size) return
      const rect = node.getBoundingClientRect()
      setSize({ width: rect.width, height: rect.height })
      setMounted(false)
    },
    [size]
  )

  const clearIdle = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }

  const positionOver = (el: Element) => {
    const r = el.getBoundingClientRect()
    setPersistentLoaderWrapperStyle(prev => ({
      ...prev,
      left: `${r.left}px`,
      top: `${r.top}px`,
      width: `${r.width}px`,
      height: `${r.height}px`,
      visibility: 'visible',
    }))
  }

  const registerOwnerLoader = useCallback<Ctx['registerOwnerLoader']>((el: Element) => {
    // someone already owns it → reject/fallback
    if (ownerLoaderRef.current) {
      return { accepted: false, unregister: () => {} }
    }

    ownerLoaderRef.current = true
    clearIdle()
    setMounted(true)
    positionOver(el)

    const updatePosition = () => positionOver(el)
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return {
      accepted: true,
      unregister: () => {
        setHexColorState(undefined)
        window.removeEventListener('scroll', updatePosition)
        window.removeEventListener('resize', updatePosition)
        ownerLoaderRef.current = false
        setPersistentLoaderWrapperStyle(prev => ({
          ...prev,
          left: `0px`,
          top: `0px`,
          width: `0px`,
          height: `0px`,
          visibility: 'hidden',
        }))
        idleTimerRef.current = window.setTimeout(() => setMounted(false), idleMs)
      },
    }
  }, [])

  const setHexColor = useCallback(
    (color: string | undefined) => {
      if (color !== hexColor) {
        setHexColorState(color)
      }
    },
    [hexColor]
  )

  const ctx = useMemo(
    () => (size ? { registerOwnerLoader, size, setHexColor } : null),
    [registerOwnerLoader, size]
  )

  return (
    <>
      {ctx ? (
        <PersistentLoaderContext.Provider value={ctx}>{children}</PersistentLoaderContext.Provider>
      ) : null}
      <div style={persistentLoaderWrapperStyle}>
        {mounted ? (
          <Box width='fit-content' height='fit-content' ref={persistentLoaderCallbackRef}>
            <Loader UNSAFE_hexColor={hexColor} />
          </Box>
        ) : null}
      </div>
    </>
  )
}

export const PersistentLoaderProvider = ({ children }: ProviderProps) => {
  const ctx = useContext(PersistentLoaderContext)
  if (ctx) {
    return <>{children}</>
  }
  return <PersistentLoaderProviderInner>{children}</PersistentLoaderProviderInner>
}

export const PersistentLoader = ({
  'data-test-id': testId,
  UNSAFE_hexColor,
  ...positioningProps
}: React.ComponentProps<typeof Loader>) => {
  const ctx = useContext(PersistentLoaderContext)
  const placeholderRef = useRef<HTMLDivElement>(null)
  const [accepted, setAccepted] = useState<boolean | null>(null)

  // useLayoutEffect to avoid conflict with suspense fallback that may happen because suspense
  // fallback is rendered and a loader is rendered during the suspended state
  // if the Provider is outside the suspense boundary, we end up with two loaders
  // useLayoutEffect makes sure the Loader is only triggered after the component is actually
  // commited to the DOM, thus not during suspended state
  useLayoutEffect(() => {
    if (!ctx) return
    if (placeholderRef.current) {
      const { accepted, unregister } = ctx.registerOwnerLoader(placeholderRef.current)
      setAccepted(accepted)
      return () => unregister()
    }
  }, [ctx?.registerOwnerLoader])

  // if not accepted or if there is no parent context, fallback to regular loader
  if (accepted === false || !ctx) {
    return <Loader data-test-id={testId} UNSAFE_hexColor={UNSAFE_hexColor} {...positioningProps} />
  }

  const { setHexColor, size } = ctx

  setHexColor(UNSAFE_hexColor)

  return (
    <Box
      data-test-id={testId}
      ref={placeholderRef}
      style={{ width: size.width, height: size.height }}
      {...positioningProps}
    />
  )
}
